import type { CSSObject } from '@emotion/react';
import { ThemeProvider, keyframes } from '@emotion/react';
import type { ThemeColors, ClassificationColors, ColorPalette } from '../utils';
import {
  mark2Color,
  classification2Color,
  darkenBy,
  lightenBy,
  setAlpha,
  themeColors,
  classificationColors,
  colorPalette,
} from '../utils';
import type { ClassificationMark } from 'bc-classification';
import type { CSSProperties } from 'react';

type SpacingType = Pick<
  CSSProperties,
  | 'padding'
  | 'paddingLeft'
  | 'paddingRight'
  | 'paddingTop'
  | 'paddingBottom'
  | 'margin'
  | 'marginLeft'
  | 'marginRight'
  | 'marginTop'
  | 'marginBottom'
  | 'gap'
>;
type SizingType = Pick<
  CSSProperties,
  'width' | 'maxWidth' | 'minWidth' | 'height' | 'maxHeight' | 'minHeight'
>;

type BorderType = Pick<
  CSSProperties,
  'borderTop' | 'borderLeft' | 'borderRight' | 'borderBottom' | 'border'
>;

type FocusType = {
  isSelected?: boolean;
  isFocused?: boolean;
  isHovering?: boolean;
  background?: boolean;
  size?: 'sm' | 'md' | 'lg';
  synced?: boolean;
  external?: boolean;
};

type Color = ThemeColors | ClassificationColors | ColorPalette | string;

const bootstrapPrefix = 'ods';

const bounceKeyframes = keyframes`
  from, 20%, 53%, 80%, to {
    transform: translate3d(0,0,0);
  }

  30%, 33% {
    transform: translate3d(0, -100%, 0);
  }

  70% {
    transform: translate3d(0, -50%, 0);
  }

  90% {
    transform: translate3d(0,-25%,0);
  }
`;

function spacing(attrs: Array<keyof SpacingType>) {
  function negativePositive(value: number) {
    return value < 0 ? `n${Math.abs(value)}` : value;
  }

  return (level: number) =>
    attrs.reduce(
      (acc, attr) => ({
        ...acc,
        [attr]: `var(--${bootstrapPrefix}-unit-${negativePositive(level)})`,
      }),
      {}
    );
}

function sizing(attr: keyof SizingType) {
  return (size: number) => ({
    [attr]: `calc(var(--px-unit) * ${size})`,
  });
}

const alphas = { sm: 0.035, md: 0.06, lg: 0.09 } satisfies { [key: string]: number };
function shadow(
  size: keyof typeof alphas = 'md',
  direction: 'left' | 'up' | 'down' | 'right' = 'down'
) {
  const color = setAlpha(themeColors.dark, alphas[size]);
  const negative = direction === 'left' || direction === 'up' ? '-' : '';
  const shadows = ['1px', '0.125rem', '0.25rem', '0.5rem', '1rem'];
  const boxShadow = [`0 0 0 1px ${color}`];

  shadows.forEach((s) => {
    const offset =
      direction === 'down' || direction === 'up' ? `0 ${negative}${s}` : `${negative}${s} 0`;
    boxShadow.push(`${offset} ${s} ${color}`);
  });

  return { boxShadow: boxShadow.join(',') };
}

const radii = {
  sm: '0.25rem',
  md: '0.375rem',
  lg: '0.5rem',
  xl: '1rem',
  xxl: '2rem',
  pill: '50rem',
  circle: '50%',
} satisfies { [key: string]: string };
function radius(size: keyof typeof radii = 'md') {
  return radii[size];
}

function border(prop: keyof BorderType): CSSObject {
  return { [prop]: `1px solid ${themeColors.border}` };
}

function getColor(color: Color) {
  const colors: { [key: string]: Color } = {
    ...themeColors,
    ...classificationColors,
    ...colorPalette,
  };

  return colors[color] ?? color;
}

function classificationBorder(
  placement: keyof BorderType,
  color: string = 'transparent'
): CSSObject {
  return { [placement]: `4px solid ${color}` };
}
function setOpacity(color: Color, alpha = 1) {
  return setAlpha(getColor(color), alpha).toString();
}

function setDarkness(color: Color, by = 0.1) {
  return darkenBy(getColor(color), by).toString();
}

function setLightness(color: Color, by = 0.1) {
  return lightenBy(getColor(color), by).toString();
}

const droppableOutlineColor = ({ dark, isBlocked }: { dark?: boolean; isBlocked?: boolean }) => {
  const color = isBlocked ? themeColors.danger : themeColors.focus;
  return dark ? lightenBy(color, 0.2) : color;
};

function outline({
  isSelected = false,
  isFocused = false,
  isHovering = false,
  background = false,
  size = 'md',
  synced = false,
  external = false,
}: FocusType = {}) {
  let focusColor: string;
  let selectedColor: string;
  let hoverColor: string;

  if (synced) {
    focusColor = themeColors.synced;
    selectedColor = lightenBy(themeColors.synced, 0.5).toString();
    hoverColor = lightenBy(themeColors.synced, 0.5).toString();
  } else {
    focusColor = themeColors.focus;
    selectedColor = themeColors.secondary;
    hoverColor = lightenBy(themeColors.secondary, 0.5).toString();
  }

  const getOutlineStyle = (outlineWidth: number, color: string) => ({
    ...(!external
      ? {
          outline: `calc(${
            outlineWidth / 16
          }rem * var(--pan-zoom-scale, 1)) solid ${color} !important`,
          boxShadow: `inset 0 0 0 1px ${themeColors.white}`,
        }
      : {
          outline: `calc(${
            outlineWidth / 16
          }rem * var(--pan-zoom-scale, 1)) dashed ${color} !important`,
          boxShadow: `0 0 0 calc(${outlineWidth / 16}rem * var(--pan-zoom-scale, 1)) ${lightenBy(
            color,
            0.6
          )}, inset 0 0 0 calc(1px * var(--pan-zoom-scale, 1)) ${themeColors.white} !important`,
        }),
    ...(background && { backgroundColor: setAlpha(color, 0.2).toString() }),
  });

  const getOutlineStyles = (outlineWidth: number) => ({
    '@media not print': {
      ...(!isSelected &&
        !isFocused && {
          ...(isHovering && getOutlineStyle(outlineWidth, hoverColor)),
          '&:hover, &:focus, &:focus-within': getOutlineStyle(outlineWidth, hoverColor),
        }),
      ...(isSelected && getOutlineStyle(outlineWidth, selectedColor)),
      '&.is-selected': getOutlineStyle(outlineWidth, selectedColor),
      ...(isFocused && getOutlineStyle(outlineWidth, focusColor)),
      '&.is-focused': getOutlineStyle(outlineWidth, focusColor),
    },
    '.is-fullscreen &': {
      outline: 'none !important',
      ...(background && { backgroundColor: 'transparent !important' }),
      boxShadow: 'none !important',
    },
  });

  const outlineWidths = {
    sm: 2,
    md: 3,
    lg: 4,
  };

  const outlineWidth = outlineWidths[size];
  const outlineStyles = getOutlineStyles(outlineWidth);

  return outlineStyles;
}

const navItem = (selected = false) => {
  const color = `var(--${bootstrapPrefix}-black-rgb)`;
  const selectedAlpha = 0.1;

  return {
    color: `rgba(${color}, ${selected ? 0.9 : 0.8})`,
    outline: 'none',
    ...(selected && {
      backgroundColor: `rgba(${color}, ${selectedAlpha})`,
    }),
    '&:hover': {
      color: `rgba(${color}, ${selected ? 1 : 0.9})`,
      backgroundColor: `rgba(${color}, ${selected ? selectedAlpha + 0.05 : 0.075})`,
    },
    '&:focus, a:focus': {
      color: `rgba(${color}, 1)`,
      boxShadow: `0 0 0 4px rgba(${color}, 0.3)`,
      outline: 'none',
    },
    '&:focus-within': {
      backgroundColor: `rgba(${color}, ${selected ? selectedAlpha : 0.05})`,
    },
    '&:active': {
      color: `rgba(${color}, 1)`,
      backgroundColor: `rgba(${color}, ${selected ? selectedAlpha + 0.075 : 0.1})`,
      '&:focus, a:focus': {
        boxShadow: 'none',
      },
    },
  };
};

type DroppeableProps = { dark?: boolean; growTarget?: boolean };

const theme = {
  backgroundColors: {
    hoverPressSelected: (color: string = themeColors.border, selected = false): CSSObject => ({
      ...(selected && { backgroundColor: color }),
      '&:focus': {
        backgroundColor: setAlpha(themeColors.focus, 0.15),
      },
      '&:hover': {
        backgroundColor: selected
          ? darkenBy(color, 0.025).toString()
          : lightenBy(color, 0.4).toString(),
      },
      '&:active': {
        backgroundColor: selected
          ? lightenBy(color, 0.1).toString()
          : darkenBy(color, 0.025).toString(),
      },
    }),
  },
  animations: {
    bounce: (time = 1, ease = 'ease'): CSSObject => ({
      animation: `${bounceKeyframes} ${time}s ${ease} forwards`,
    }),
  },
  borders: {
    borderTop: border('borderTop'),
    borderStart: border('borderLeft'),
    borderEnd: border('borderRight'),
    borderBottom: border('borderBottom'),
    border: border('border'),
    borderRadius: radius,
    resize: (isCard?: boolean, isActive?: boolean, isClipping = false): CSSObject => ({
      ...(isActive && {
        outline: `0.125rem solid ${themeColors.secondary}`,
        outlineOffset: '-0.0625rem',
        '.is-focused &': {
          outlineColor: isCard || isClipping ? themeColors.synced : themeColors.focus,
        },
      }),
    }),
    widget: (hasBorder?: boolean): CSSObject => {
      if (hasBorder)
        return {
          outline: `2px solid ${themeColors.border}`,
          outlineOffset: '-1px',
        };
      return {
        td: {
          outline: 'none !important',
        },
      };
    },
  },
  classification: {
    color: classification2Color,
    borderBottom: (color?: string) => classificationBorder('borderBottom', color),
    borderTop: (color?: string) => classificationBorder('borderTop', color),
    colorFromMark: (mark?: ClassificationMark) => {
      return mark ? mark2Color(mark) : undefined;
    },
    badge: (height = 20, minWidth?: number | string): CSSObject => {
      const fontSize = height * 0.6;
      const lineHeight = fontSize * 1.5;
      const padding = (height - lineHeight) / 2;
      return {
        lineHeight: theme.sizing.pixels(lineHeight),
        padding: `${theme.sizing.pixels(padding)} ${theme.sizing.pixels(8)}`,
        fontWeight: 700,
        textTransform: 'uppercase',
        fontSize: theme.sizing.pixels(fontSize),
        letterSpacing: '0.025em',
        fontFamily: 'var(--ods-font-sans-serif)',
        textAlign: 'center',
        wordWrap: 'break-word',
        wordBreak: 'break-word',
        ...(minWidth && { minWidth: theme.sizing.pixels(minWidth) }),
      };
    },
  },
  colors: themeColors,
  dnd: {
    draggable: (isDragging?: boolean, shouldSetCursor?: boolean): CSSObject => ({
      ...(isDragging && {
        filter: `drop-shadow(${shadow('md')})`,
        pointerEvents: 'auto !important' as 'auto',
      }),
      ...(shouldSetCursor && {
        cursor: isDragging ? 'grabbing' : 'grab',
      }),
    }),
    droppable: (
      isDragging: boolean,
      isDraggingOver: boolean,
      isBlocked?: boolean,
      { dark, growTarget }: DroppeableProps = {
        dark: false,
        growTarget: true,
      }
    ): CSSObject => ({
      outlineColor: 'transparent',
      ...(isDragging && {
        ...(growTarget && { flexGrow: 1, minHeight: '2rem' }),
        outlineStyle: !isBlocked ? 'dashed !important' : 'solid',
        outlineWidth: !isBlocked ? '0.0625rem' : '0.125rem',
        outlineOffset: !isBlocked ? '-0.0625rem' : '-0.125rem',
        outlineColor: `${droppableOutlineColor({
          dark,
          isBlocked,
        })} !important`,
        outlineRadius: 'auto',
        opacity: '1 !important',
      }),
      ...(isDraggingOver && {
        backgroundColor: isBlocked ? setOpacity('danger', 0.1) : setOpacity('focus', 0.15),
      }),
      transition: 'outline-color 0.2s, background-color 0.2s',
    }),
    handle: (isDragging: boolean): CSSObject => ({
      color: `${themeColors.dark} !important`,
      cursor: 'grab',
      opacity: 0.25,
      visibility: 'hidden',
      ...(isDragging && {
        cursor: 'grabbing',
        pointerEvents: 'auto !important' as 'auto',
        visibility: 'visible',
      }),
      '*:hover > &, &:hover': {
        visibility: 'visible',
      },
    }),
  },
  position: {
    positionRelative: { position: 'relative !important' as 'relative' } as CSSObject,
    positionAbsolute: { position: 'absolute !important' as 'absolute' } as CSSObject,
  },
  display: {
    dBlock: { display: 'block' } as CSSObject,
    dInlineBlock: { display: 'inline-block' } as CSSObject,
  },
  flex: {
    dFlex: { display: 'flex' } as CSSObject,
    dInlineFlex: { display: 'inline-flex' } as CSSObject,
    flex1: { flex: 1 } as CSSObject,
    flexColumn: { flexDirection: 'column' } as CSSObject,
    flexGrow1: { flexGrow: 1 } as CSSObject,
    flexShrink0: { flexShrink: 0 } as CSSObject,
    alignItemsCenter: { alignItems: 'center' } as CSSObject,
    alignItemsBaseLine: { alignItems: 'baseline' } as CSSObject,
    alignItemsStretch: { alignItems: 'stretch' } as CSSObject,
    alignSelfCenter: { alignSelf: 'center' } as CSSObject,
    alignContentCenter: { alignContent: 'center' } as CSSObject,
    justifyContentCenter: { justifyContent: 'center' } as CSSObject,
    justifyContentEnd: { justifyContent: 'end' } as CSSObject,
    justifyContentFlexEnd: { justifyContent: 'flex-end' } as CSSObject,
  },
  focus: {
    outline,
    inlineText: {
      backgroundColor: `${setAlpha(themeColors.secondary, 0.1)} !important`,
      boxShadow: `0 0 0 0.125rem ${setAlpha(themeColors.secondary, 0.1)} !important`,
      '&:focus': {
        backgroundColor: `${setAlpha(themeColors.secondary, 0.15)} !important`,
        boxShadow: `0 0 0 0.125rem ${setAlpha(themeColors.secondary, 0.15)} !important`,
      },
    } as CSSObject,
    input: {
      borderColor: `${themeColors.focus} !important`,
      boxShadow: `0 0 0 0.25rem ${setAlpha(themeColors.focus, 0.25)}`,
    } as CSSObject,
  },
  nav: {
    item: navItem,
  },
  highlight: (color: string) => setOpacity(color, 0.1),
  rgba: setOpacity,
  darken: setDarkness,
  lighten: setLightness,
  shadows: {
    shadow,
  },
  sizing: {
    width: sizing('width'),
    maxWidth: sizing('maxWidth'),
    minWidth: sizing('minWidth'),
    height: sizing('height'),
    maxHeight: sizing('maxHeight'),
    minHeight: sizing('minHeight'),
    pixels: (px: number | string) => `calc(var(--px-unit) * ${px})`,
  },
  spacing: {
    p: spacing(['padding']),
    px: spacing(['paddingLeft', 'paddingRight']),
    py: spacing(['paddingTop', 'paddingBottom']),
    ps: spacing(['paddingLeft']),
    pe: spacing(['paddingRight']),
    pt: spacing(['paddingTop']),
    pb: spacing(['paddingBottom']),
    m: spacing(['margin']),
    mx: spacing(['marginLeft', 'marginRight']),
    my: spacing(['marginTop', 'marginBottom']),
    ms: spacing(['marginLeft']),
    me: spacing(['marginRight']),
    mt: spacing(['marginTop']),
    mb: spacing(['marginBottom']),
    gap: spacing(['gap']),
    map: '0.5rem',
  },
  text: {
    size: {
      smaller: '0.625rem',
      small: '0.75rem',
    },
    alignment: {
      center: { textAlign: 'center' } as CSSObject,
      left: { textAlign: 'left' } as CSSObject,
      right: { textAlign: 'right' } as CSSObject,
    },
    wrap: {
      truncate: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      } as CSSObject,
      wrap: { whiteSpace: 'normal !important' as 'normal' } as CSSObject,
      noWrap: { whiteSpace: 'nowrap !important' as 'nowrap' } as CSSObject,
    },
    decoration: {
      underlineHighlight: {
        textDecoration: 'underline dotted',
        cursor: 'pointer',
        '&:hover': { color: themeColors.primary },
      } as CSSObject,
    },
    transform: {
      uppercase: { textTransform: 'uppercase !important' as 'uppercase' } as CSSObject,
    },
    clamp: (clamp: number | 'none' = 'none'): CSSObject => ({
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      WebkitBoxOrient: 'vertical',
      display: '-webkit-box',
      WebkitLineClamp: clamp,
    }),
  },
  zIndex: {
    contentOverlay: 100,
    floatingControls: 900,
    // See: https://getbootstrap.com/docs/5.2/layout/z-index/
    dropdown: 1000,
    sticky: 1020,
    fixed: 1030,
    offcanvasBackdrop: 1040,
    offcanvas: 1045,
    modalBackdrop: 1050,
    modal: 1055,
    popover: 1070,
    tooltip: 1080,
    toast: 1090,
  },
};

type CustomTheme = typeof theme;

const BootstrapThemeProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <ThemeProvider theme={theme}>{children}</ThemeProvider>
);

export default BootstrapThemeProvider;
export type { CustomTheme, FocusType };
